/**
 * The offer itself — a strip along the bottom of the page, in the site's own
 * housing rather than a browser-shaped card.
 *
 * Two shapes behind one panel, chosen by `useInstallOffer`
 * (@bindery/ui/hooks/use-install-offer), over this app's record in ./store.ts:
 *
 *  - **native**: a button. The browser owns the dialog; the site owns only
 *    *when* it is asked for.
 *  - **manual** (iOS): a button that unfolds three steps in place. Deliberately
 *    folded to start — the offer's job on first sight is to be refusable in one
 *    tap, and a panel that opens at the height of a tutorial is a panel that
 *    covers the page it is interrupting. Unfolding in place rather than in a
 *    modal sheet is what lets the visitor keep the steps on screen while they
 *    reach for the browser's own Share button, which is the entire point:
 *    those two things have to be visible at the same time.
 *
 * Not a modal, so no focus trap and no inert background: this interrupts, it
 * does not block. Escape still closes it, because a thing that appears
 * uninvited over the page should answer to the key that dismisses everything
 * else on the web — but because nothing traps focus here, that same Escape
 * may be meant for something else entirely, so it closes without spending one
 * of the three strikes a real "not now" does; see `closeAndRelease` below.
 * Refusing to trap focus is not the same as being allowed to throw it away,
 * though — see `releaseFocus` below for what happens to the caret when the
 * panel is the thing that leaves.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { InstallPlatform } from "@bindery/ui/lib/install-platform";
import { useInstallOffer } from "@bindery/ui/hooks/use-install-offer";
import { SITE_NAME } from "../site";
import { CardSection } from "./CardSection";
import { installState } from "./store";

/**
 * The two glyphs the steps point at, drawn to match the ones iOS actually
 * shows — someone scanning the Share sheet is looking for a shape, not a word,
 * and a wrong shape sends them past the row they need.
 *
 * `inline-block` and the negative baseline shift are not decoration: Tailwind's
 * preflight sets `svg { display: block }`, which inside a sentence puts the
 * glyph on its own line. Both of these sit *in* a sentence.
 */
function ShareGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="mx-0.5 inline-block size-4 align-[-0.22em]"
    >
      <path d="M12 15V3" />
      <path d="M8 6.5 12 2.5l4 4" />
      <path d="M6 11H4.5v10.5h15V11H18" />
    </svg>
  );
}

function AddGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="mx-0.5 inline-block size-4 align-[-0.22em]"
    >
      <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
      <path d="M12 8.5v7M8.5 12h7" />
    </svg>
  );
}

function CloseGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
      className="size-3.5"
    >
      <path d="M5 5l14 14M19 5 5 19" />
    </svg>
  );
}

/**
 * "Home screen" or "computer" — the difference between a phone and a laptop is
 * the one thing the visitor can check against the sentence, so getting it
 * wrong is the fastest way to read as boilerplate.
 */
function headline(platform: InstallPlatform): string {
  return platform === "desktop"
    ? `Install ${SITE_NAME} on this computer`
    : `Add ${SITE_NAME} to your home screen`;
}

/**
 * The three steps, unchanged on iOS for about a decade.
 *
 * "the browser toolbar" rather than "Safari's toolbar": since iOS 16.4 the
 * Share sheet in Chrome and Firefox carries Add to Home Screen too, and the
 * toolbar is where the button is in all of them — at the bottom on a phone,
 * at the top on an iPad. Naming a browser we have not actually detected would
 * be the one instruction here capable of being wrong.
 */
function IosSteps({ id, hidden }: { id: string; hidden: boolean }) {
  return (
    <ol
      id={id}
      // Rendered collapsed too, as `hidden`, so the toggle's aria-controls
      // always names an element that exists.
      hidden={hidden}
      // Tailwind's preflight sets `ol,ul,menu { list-style: none }`, and WebKit
      // strips list semantics from any list styled that way — so on iOS
      // VoiceOver, the one platform these steps exist for, an unadorned `<ol>`
      // announces as three loose paragraphs with no position in a sequence.
      // `role="list"` puts the semantics back, which is also what makes the
      // `aria-hidden` on the drawn numbers below safe to rely on. Removing it
      // in favour of a CSS fix needs a VoiceOver check on a device (kanban #20).
      // oxlint-disable-next-line jsx-a11y/no-redundant-roles -- restores the list semantics WebKit strips under preflight's list-style: none
      role="list"
      className="mt-3.5 space-y-2.5 border-t pt-3.5"
    >
      <li className="text-muted-foreground flex gap-2.5 text-sm leading-6">
        <StepNumber n={1} />
        <span>
          Tap <ShareGlyph /> <span className="text-foreground">Share</span> in
          the browser toolbar
        </span>
      </li>
      <li className="text-muted-foreground flex gap-2.5 text-sm leading-6">
        <StepNumber n={2} />
        <span>
          Scroll down to <AddGlyph />{" "}
          <span className="text-foreground">Add to Home Screen</span>
        </span>
      </li>
      <li className="text-muted-foreground flex gap-2.5 text-sm leading-6">
        <StepNumber n={3} />
        <span>
          Tap <span className="text-foreground">Add</span>
        </span>
      </li>
    </ol>
  );
}

/**
 * `aria-hidden`, because the list is already an `<ol>`: a screen reader
 * announces "1 of 3" from the markup, and the drawn circle would make that
 * "one, one of three". That premise is only true because of the explicit
 * `role="list"` above — without it, on iOS, the positional announcement this
 * defers to is the announcement that goes missing.
 */
function StepNumber({ n }: { n: number }) {
  return (
    <span
      className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border font-mono text-[0.62rem]"
      aria-hidden="true"
    >
      {n}
    </span>
  );
}

/**
 * What installing actually gets you — and the two shapes of the offer do not
 * get you the same thing, so they are not told the same sentence.
 *
 * Neither line promises full screen: `"display": "standalone"` in
 * public/manifest.webmanifest takes away the browser's own toolbars and leaves
 * the status bar and the home indicator exactly where they were. A window of
 * its own, not full screen.
 *
 * The offline claim is deliberately the weak one that public/sw.js can
 * actually keep. An installed Chromium app is the same origin in the same
 * storage as the tab it was installed from, so the registered worker and its
 * cache are already there — and a page that has been fetched has been cached:
 * the fetch handler caches every same-origin GET it can, and the visit that
 * installed the worker hands it what it loaded first (`offerLoadedFiles` in
 * src/registerServiceWorker.ts). It knows nothing about which pages anyone
 * has read.
 *
 * iOS gets a different sentence because iOS is a different container: a
 * home-screen web app there is walled off from the browser's storage, so the
 * app's first launch finds no registered worker, an empty cache, and none of
 * whatever the site had persisted. Telling someone their data comes with them
 * is precisely the promise that first launch breaks, and this panel is the
 * last moment at which saying so is still useful.
 */
function subhead(kind: "native" | "manual"): string {
  return kind === "native"
    ? "Opens in a window of its own, and pages you have already visited stay readable offline."
    : "Opens without the browser's toolbars. iOS gives it storage of its own, so it starts fresh — anything saved here stays in the browser.";
}

/**
 * The one thing ever put into the live region, and why there is one.
 *
 * The panel is inserted a couple of seconds after load, at the very end of the
 * document. It is a properly named `region` landmark, so it can be found — but
 * only by someone who thinks to go looking, and nothing tells them to. This is
 * the telling: new controls exist, they are at the end, and Escape now means
 * something it did not mean a moment ago.
 *
 * It is a separate `<output>` line inside the dock rather than `aria-live`
 * on the `<section>` on purpose. A live region wrapping the panel would re-read
 * the whole offer every time the disclosure folded or unfolded, which is the
 * one interaction this panel has.
 */
const ARRIVAL_ANNOUNCEMENT =
  "Install offer added at the end of the page. Press Escape to dismiss it.";

/**
 * A live region only announces a *change*, so it has to be in the accessibility
 * tree and empty before its text arrives. Rendering the sentence in the same
 * commit that inserts the element is the classic way to have it read out
 * nowhere; a tick's gap is the classic way to fix it.
 */
const ANNOUNCE_DELAY_MS = 120;

export function InstallOffer() {
  const { offer, install, dismiss, close } = useInstallOffer(installState);
  const [stepsShown, setStepsShown] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const announcedRef = useRef(false);
  const dockRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const stepsId = useId();

  const offered = offer.kind !== "hidden";

  /**
   * Hand focus back to the page before the panel stops existing.
   *
   * Dismissing is an activation of a button inside a subtree that is about to
   * be removed, and when the browser removes the focused element it drops
   * focus to `<body>` — which is not "nowhere", it is "the top", so the next
   * Tab starts the document over at the site header. The panel is the last
   * thing in the DOM, so a keyboard user has by definition already passed
   * everything else to reach it; starting them again is the whole cost.
   *
   * `<main>` is the target because it is the landmark the reading position is
   * inside, and the only stable one that outlives this element: focus lands in
   * the page content, Shift+Tab goes back toward whatever they were on, and a
   * screen reader says "main" rather than re-reading from the top. It is given
   * `tabindex="-1"` on the way in, which makes it focusable by script without
   * adding it to anybody's tab order.
   *
   * `preventScroll`, because focusing an element scrolls it into view and
   * `<main>` begins just under the header — without it, dismissing would yank
   * the page back to the top, which is the thing this is trying to avoid. The
   * option lands at Safari 15, above BROWSER_FLOOR's safari14; below that it is
   * an ignored property on an options object, so the oldest supported browsers
   * get the scroll and every browser gets the focus. That is the right way
   * round.
   *
   * Still not a focus trap. The module comment argues against one and is
   * right — focus is free to leave this panel at any moment. This is only
   * about where focus goes when the panel is the thing doing the leaving.
   */
  const releaseFocus = useCallback(() => {
    const dock = dockRef.current;
    const active = document.activeElement;
    if (dock === null || active === null || !dock.contains(active)) return;
    const main = document.querySelector<HTMLElement>("main");
    if (main === null) return;
    main.tabIndex = -1;
    main.focus({ preventScroll: true });
  }, []);

  const dismissAndRelease = useCallback(() => {
    releaseFocus();
    dismiss();
  }, [releaseFocus, dismiss]);

  // Escape's own handler, below, closes rather than dismisses: a bubble-phase
  // `window` keydown listener sees every Escape on the page, not just one
  // meant for this panel — a combobox, a dropdown, any dialog the project
  // later adds. `close` (@bindery/ui/hooks/use-install-offer) takes the panel down the same
  // as a real "not now" does, but does not spend one of the three strikes
  // the shared install state counts towards retiring the offer, because an Escape aimed at
  // something else was never an answer about installing.
  const closeAndRelease = useCallback(() => {
    releaseFocus();
    close();
  }, [releaseFocus, close]);

  // Focus moves on the click rather than when the panel finally comes down,
  // because what comes down is decided a promise later by the browser's own
  // dialog (see `install` in @bindery/ui/hooks/use-install-offer) and there is no commit here
  // to hang it off. Moving it early is invisible: the browser's install dialog
  // takes over the screen in the same gesture.
  const installAndRelease = useCallback(() => {
    releaseFocus();
    install();
  }, [releaseFocus, install]);

  const toggleSteps = useCallback(() => {
    setStepsShown((shown) => !shown);
  }, []);

  useEffect(() => {
    if (!offered) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // "Esc" is the pre-`key`-standard spelling still sent by the oldest
      // browsers inside BROWSER_FLOOR (packages/config/vite.ts).
      if (event.key === "Escape" || event.key === "Esc") closeAndRelease();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [offered, closeAndRelease]);

  useEffect(() => {
    if (!offered || announcedRef.current) return;
    const timer = setTimeout(() => {
      announcedRef.current = true;
      setAnnouncement(ARRIVAL_ANNOUNCEMENT);
    }, ANNOUNCE_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [offered]);

  // Reserve the strip the dock covers, so the browser has somewhere to stop
  // when it scrolls a focused element into view — see `.app-install-open` in
  // ../index.css for what that fixes.
  useEffect(() => {
    if (!offered) return;
    const root = document.documentElement;
    root.classList.add("app-install-open");
    return () => {
      root.classList.remove("app-install-open");
    };
  }, [offered]);

  // Measured, not guessed: the dock is a different height on the two shapes of
  // the offer and again once the steps unfold, and a reserved strip that is
  // wrong in either direction is either a gap or a hole in the page. Remeasured
  // on resize because that includes a phone being turned sideways, which is the
  // case where the number changes most.
  useEffect(() => {
    if (!offered) return;
    const root = document.documentElement;
    const measure = () => {
      const dock = dockRef.current;
      if (dock === null) return;
      const { height } = dock.getBoundingClientRect();
      root.style.setProperty("--app-install-dock-h", `${Math.ceil(height)}px`);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      root.style.removeProperty("--app-install-dock-h");
    };
  }, [offered, stepsShown, offer.kind]);

  // Rendering nothing until an effect has decided otherwise is what keeps the
  // prerendered markup and the first client render identical — see the comment
  // at the top of @bindery/ui/hooks/use-install-offer. Dismissal lands here too: once the
  // offer is retired the panel really is gone, not merely hidden.
  if (!offered) return null;

  return (
    <div ref={dockRef} className="app-install-dock">
      {/* <output> is a status region by default (jsx-a11y/prefer-tag-over-role
          over role="status"); aria-live is spelled out because not every
          screen reader treats <output> as live on its own. */}
      <output className="sr-only" aria-live="polite">
        {announcement}
      </output>
      {/* A labelled <section> is a region: non-modal by design (see the module
          comment), because role="dialog" would promise a focus trap and an
          inert page, neither of which is true here.

          The padding is utilities, not CSS on .app-install-panel
          (../index.css): a components-layer padding loses to any `p-*`
          utility on the same element (AGENTS.md, CSS layering), and
          CardSection carries `py-6`. cn() lets `py-4` replace it outright. */}
      <CardSection
        className="app-install-panel relative gap-0 px-[1.1rem] py-4 shadow-lg"
        aria-labelledby={titleId}
      >
        {/* Sized to 44px of touch target with padding while drawing as a 14px
            glyph. */}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground absolute top-0 right-0 flex size-11 items-center justify-center rounded-md"
          onClick={dismissAndRelease}
          aria-label="Not now"
        >
          <CloseGlyph />
        </button>

        {/* The scrolling region, so a panel that outgrows a landscape phone
            can be read to the end instead of growing off the top of it. The
            close button is deliberately its sibling rather than its child, so
            it stays pinned to the housing while this scrolls. */}
        <div className="app-install-scroll">
          {/* `pr-7` keeps the title clear of the close button, which is
              absolutely positioned over this row's top-right corner. */}
          <div className="pr-7">
            <p className="text-muted-foreground font-mono text-[0.68rem] tracking-[0.16em] uppercase">
              Install
            </p>
            <p id={titleId} className="mt-1 text-[0.95rem] leading-snug">
              {headline(offer.platform)}
            </p>
            <p className="text-muted-foreground mt-1 text-sm leading-snug">
              {subhead(offer.kind)}
            </p>
          </div>

          {offer.kind === "native" ? (
            <button
              type="button"
              className="bg-primary text-primary-foreground hover:bg-primary/90 mt-3.5 h-10 w-full rounded-md text-sm font-medium"
              onClick={installAndRelease}
            >
              Install
            </button>
          ) : (
            // A disclosure, not a swap: the button stays put and keeps its
            // `aria-expanded` truthful. Replacing it with the steps would leave
            // a screen reader that had just activated it with nothing focused
            // and no statement of what changed.
            <>
              <button
                type="button"
                className="hover:bg-accent mt-3.5 h-10 w-full rounded-md border text-sm font-medium"
                onClick={toggleSteps}
                aria-expanded={stepsShown}
                aria-controls={stepsId}
              >
                {stepsShown ? "Hide steps" : "Show me how"}
              </button>
              <IosSteps id={stepsId} hidden={!stepsShown} />
            </>
          )}
        </div>
      </CardSection>
    </div>
  );
}
